import { Accordion } from "@au-schein-de/au-ui-library";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FiltersProvider } from "./filters.context";
import {
	handleFilterTriggerClick,
	handleFilterTriggerKeyDown,
} from "./scroll-filter-into-view";
import { CategoriesFilter } from "./sections/categories-filter.component";
import { ComplaintsFilter } from "./sections/complaints-filter.component";
import { DeliveryVendorFilter } from "./sections/delivery-vendor-filter.component";
import { EffectsFilter } from "./sections/filtersV1/effects-filter.component";
import { ProducersFilter } from "./sections/filtersV1/producer-filter.component";
import { GeneticsFilter } from "./sections/genetics-filter.component";
import { ThcCbdFilter } from "./sections/thc-cbd-filter.component";
import { TopProducersLowPriceFilter } from "./sections/top-producers-low-price-filter.component";
import type { FilterSetter, FiltersConfig, FiltersState } from "./types";
import { getActiveFilterGroupsCount } from "./utils";

interface ProductFiltersProps {
	filters: FiltersState;
	config: FiltersConfig;
	onFilterChange: FilterSetter;
	onTopProducerWarning?: () => void;
}

export const ProductFilters: FC<ProductFiltersProps> = ({
	filters,
	config,
	onFilterChange,
	onTopProducerWarning,
}) => {
	const { t } = useTranslation("product");

	const activeFiltersCount = getActiveFilterGroupsCount(filters);

	return (
		<FiltersProvider
			filters={filters}
			config={config}
			onFilterChange={onFilterChange}
			disableTopProducersFilter={false}
			onTopProducerWarning={onTopProducerWarning}
		>
			<aside
				className="max-h-[85vh] md:overflow-auto px-1 -mx-1"
				onClick={handleFilterTriggerClick}
				onKeyDown={handleFilterTriggerKeyDown}
			>
				<div>
					<div className="hidden md:block mb-8">
						<DeliveryVendorFilter />
					</div>
					<div className="hidden md:flex items-center justify-between mb-4">
						<h2 className="text-body text-gray-500">
							{t("filters")}{" "}
							{activeFiltersCount > 0 && `(${activeFiltersCount})`}
						</h2>
					</div>
					<Accordion.Root
						type="multiple"
						className="space-y-2"
						defaultValue={[]}
					>
						<ComplaintsFilter />

						<ProducersFilter>
							<TopProducersLowPriceFilter />
						</ProducersFilter>

						<GeneticsFilter />

						<ThcCbdFilter />

						<CategoriesFilter />

						<EffectsFilter />
					</Accordion.Root>
				</div>
			</aside>
		</FiltersProvider>
	);
};
